import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { GraduationCap, ArrowLeft, MapPin, Star } from 'lucide-react';

export const CoachesShowcase = () => {
  const { API } = useContext(AuthContext);
  const [coaches, setCoaches] = useState([]);

  useEffect(() => {
    axios.get(`${API}/coaches`)
      .then((res) => setCoaches((res.data || []).slice(0, 4)))
      .catch(() => {});
  }, []);

  if (coaches.length === 0) return null;

  return (
    <div className="relative mt-12 overflow-hidden rounded-3xl shadow-2xl" data-testid="home-coaches-section">
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-purple-50/60 to-white"></div>
      <div className="absolute -top-32 -right-32 w-96 h-96 bg-indigo-300/25 rounded-full blur-3xl"></div>
      <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-purple-300/25 rounded-full blur-3xl"></div>

      <div className="relative z-10 p-6 sm:p-10">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-2xl flex items-center justify-center shadow-lg">
              <GraduationCap className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-black text-gray-900">🎓 מאמני טניס</h2>
              <p className="text-gray-600 text-sm sm:text-base">מאמנים מקצועיים לשיעורים פרטיים וקבוצתיים</p>
            </div>
          </div>
          <Link to="/coaches" data-testid="home-coaches-cta">
            <Button className="bg-gradient-to-l from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-full font-bold px-6 shadow-lg">
              לכל המאמנים
              <ArrowLeft className="w-4 h-4 mr-2" />
            </Button>
          </Link>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-6">
          {coaches.map((c) => (
            <Link key={c.id} to={`/coaches/${c.id}`} data-testid={`home-coach-card-${c.id}`}>
              <Card className="hover:shadow-xl hover:-translate-y-1 transition-all h-full border-indigo-100 text-center">
                <CardContent className="p-4">
                  <Avatar className="w-16 h-16 mx-auto border-3 border-indigo-200 mb-2">
                    {c.image_thumb && <AvatarImage src={c.image_thumb} alt={c.full_name} className="object-cover" />}
                    <AvatarFallback className="bg-indigo-100 text-indigo-700 font-black text-xl">{(c.full_name || '?').charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div className="font-black text-gray-800 text-sm truncate">{c.full_name}</div>
                  {c.total_reviews > 0 && (
                    <div className="flex items-center justify-center gap-1 text-xs text-amber-500 font-bold mt-0.5">
                      <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                      {Number(c.average_rating || 0).toFixed(1)}
                      <span className="text-gray-400">({c.total_reviews})</span>
                    </div>
                  )}
                  {(c.specialties || []).slice(0, 2).map((s) => (
                    <Badge key={s} variant="outline" className="text-[10px] text-indigo-600 border-indigo-200 mt-1 mx-0.5">{s}</Badge>
                  ))}
                  {(c.area || c.city) && (
                    <div className="flex items-center justify-center gap-0.5 mt-1.5 text-[11px] text-gray-400">
                      <MapPin className="w-3 h-3" />{c.area || c.city}
                    </div>
                  )}
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};
